import { useEffect, MutableRefObject, useState } from 'react';

import { MarkerPopupConfig } from '../../components/map/hook/marker-popup-factory';
import { CrzpMap } from '../../components/map/hook/map-hook';
import { getCoordinates } from '../../components/map/map-api';
import { Source, Area } from './overview-map-types';
import {
  createLeafletMarker,
  createMarkerIcon,
} from '../../components/map/marker';
import { MarkerType } from '../../components/map/map-types';

const mapAreaToMarkerType: Record<Area, MarkerType> = {
  IRZ_FACILITY: MarkerType.FILLED_BLUE,
  OVZ_FACILITY: MarkerType.FILLED_RED,
  PRODUCTION_SITE: MarkerType.FILLED_GREY,
};

const isLoading = (loading: Record<number, boolean>) =>
  Object.values(loading).some((v) => v);

export function useOverviewMapSources({
  map,
  source,
  popupConfigs,
  minimumZoomToLoad,
}: {
  map: MutableRefObject<CrzpMap | null>;
  source: Source[];
  popupConfigs: Record<Area, MarkerPopupConfig<any>>;
  minimumZoomToLoad: number;
}) {
  const [loading, setLoading] = useState(
    source.reduce((acc, _, index) => {
      acc[index] = !source[index].shouldLoad;
      return acc;
    }, {} as Record<number, boolean>)
  );

  useEffect(() => {
    const leftCorner = map.current?.getLeftTopCornerCoordinates();
    const rightCorner = map.current?.getRightBottomCornerCoordinates();

    const fetchData = async (s: Source, sourceIndex: number) => {
      if (!s.shouldLoad || !s.api || !leftCorner || !rightCorner) {
        return;
      }

      setLoading((p) => ({ ...p, [sourceIndex]: true }));

      const coordinates = await getCoordinates(
        leftCorner,
        rightCorner,
        s.filters,
        s.api
      );

      setLoading((p) => {
        return { ...p, [sourceIndex]: false };
      });

      // chunking markers to avoid browser crash
      let index = 0;
      const icon = createMarkerIcon(mapAreaToMarkerType[s.type], 'small');

      function addMarkers() {
        const chunk = coordinates.slice(index, index + 1000).map((m) =>
          createLeafletMarker({
            ...m,
            customIcon: icon,
          })
        );

        map.current?.addLeafletMarkers(chunk, {
          markerPopupConfig: popupConfigs[s.type],
        });
        index += 1000;

        if (index < coordinates.length) {
          requestAnimationFrame(addMarkers);
        }
      }

      requestAnimationFrame(addMarkers);
    };

    if (
      minimumZoomToLoad &&
      (map.current?.ref?.getZoom() ?? 0) >= minimumZoomToLoad
    ) {
      map.current?.clearMarkers();
      map.current?.showLoader();

      source.forEach((s, index) => {
        fetchData(s, index);
      });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    source,
    map.current,
    map.current?.ref?.getZoom(),
    JSON.stringify(map.current?.ref?.getBounds()),
  ]);

  useEffect(() => {
    if (!isLoading(loading)) {
      map.current?.hideLoader();
    } else {
      map.current?.showLoader();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, map.current]);

  return loading;
}
